import {
  DragOverlay,
  KeyboardSensor,
  type Modifier,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  type DragEndEvent,
  type DragMoveEvent,
  type ItemDefinition,
  TimelineContext,
  groupItemsToSubrows,
  useTimelineContext,
  useTimelineMonitor,
} from 'dnd-timeline';
import { Fragment, type ReactNode, useMemo, useState } from 'react';
import { carIdOfRow, copyId } from '../grouping.js';
import type { ScheduleDensity } from '../scheduleDensity.js';
import type { ScheduleBar, ScheduleCar } from '../types.js';
import { BarItem } from './BarItem.js';
import { CarRow, Subrow } from './CarRow.js';
import { DayCellItem } from './DayCellItem.js';
import { DayShading } from './DayShading.js';
import { FreeCells } from './FreeCells.js';
import { GroupHeader, type GroupView } from './GroupHeader.js';
import { NowMarker } from './NowMarker.js';
import { OccupancyRow } from './OccupancyRow.js';
import { TimeAxis } from './TimeAxis.js';
import { gapShades } from './bufferShading.js';
import { type DayCell, splitDayCells } from './dayCells.js';
import {
  type CreateProposal,
  DRAG_THRESHOLD_PX,
  type DragAbilities,
  type DragProposal,
  type MoveEstimate,
  type ResizeProposal,
  type ScheduleView,
  type TimeRange,
  allowedAxes,
  axisLockModifier,
  canResize,
  lockedAxis,
  moveEstimate,
  movingSpan,
  mytPeriod,
  resolveCreate,
  resolveDrop,
  resolveResize,
} from './dropLogic.js';
import type { OccupancySummary } from './occupancy.js';

export interface ScheduleTimelineProps {
  view: ScheduleView;
  range: TimeRange;
  cars: ScheduleCar[];
  bars: ScheduleBar[];
  /** 328: every bar of the response, UNFILTERED, for the clash estimate while dragging (a booking the
   *  status filter hides still occupies its car). Defaults to `bars`. */
  allBars?: ScheduleBar[];
  now: number;
  /** 396: with the clicked bar's own rectangle (a car drawn twice anchors its card to the copy clicked). */
  onOpenBar?: (bar: ScheduleBar, rect?: DOMRect) => void;
  /** 368 (#1236, FR-004): a day cell's "Open day", or a Week/Month day heading — show that date in the Day view. */
  onOpenDay?: (date: string) => void;
  /** 316 (US1): what the caller may drag. Absent means read only. */
  abilities?: DragAbilities;
  /** 316 (US1): one proposal per drag, reported at release; the caller confirms and saves. */
  onProposeMove?: (proposal: DragProposal, anchorRect: DOMRect) => void;
  /** 316 (US4, US5): one edge of a block or an unpaid booking dragged. */
  onProposeResize?: (proposal: ResizeProposal, anchorRect: DOMRect) => void;
  /** 316 (US4): a new block drawn on an empty stretch of a car's row. */
  onProposeCreate?: (proposal: CreateProposal, anchorRect: DOMRect) => void;
  /** 317: the occupancy figures, computed once per response by the caller (R6). */
  occupancy?: OccupancySummary;
  /** 318: row density, a per-viewer preference held by the page. */
  density?: ScheduleDensity;
  /** 319: rows gathered under collapsible group headings; absent means the single list. */
  grouping?: {
    /** 396: `host` draws a car under each of its hosts (a copy per group). */
    mode: 'location' | 'category' | 'host';
    groups: GroupView[];
    collapsed: ReadonlySet<string>;
    onToggle: (key: string) => void;
  };
  /** 329 (FR-004): the timezone and counts line, drawn in the axis's corner above the car names. */
  corner?: ReactNode;
}

const READ_ONLY: DragAbilities = { canMoveBooking: false, canEditBlocks: false };

const SIDEBAR_WIDTH = 176;
/** 316 (FR-021): on a phone the car column narrows, so the grid keeps most of the width. */
const SIDEBAR_WIDTH_NARROW = 112;
const sidebarWidthFor = (viewportWidth: number) =>
  viewportWidth < 640 ? SIDEBAR_WIDTH_NARROW : SIDEBAR_WIDTH;

/**
 * 316 (FR-021): the least width a view is drawn at. Below it the grid scrolls sideways INSIDE its
 * own container (the page never does), with the car column pinned — so a tablet reaches the whole
 * period instead of squeezing it until bars are unreadable.
 */
function minTimelineWidth(view: ScheduleView, range: TimeRange, sidebarWidth: number): number {
  const days = Math.round((range.end - range.start) / 86_400_000);
  // Day at 36 px an hour fits a 1440 px desktop without scrolling (40 did not: seen in T027).
  const perUnit = view === 'day' ? 24 * 36 : view === 'week' ? days * 110 : days * 32;
  return sidebarWidth + perUnit;
}
const noop = () => {};
// The default pan strategy takes over Ctrl+wheel (the browser's zoom) on the grid. The view is
// chosen with the toolbar, never by panning, so no strategy is installed.
const noPan = () => {};

/**
 * 316 (US2, US3) — the fleet timeline on `dnd-timeline`: cars as rows, periods as bars, the axis
 * and the now-line in Malaysia time. T016 is read only; drag (T020), resize and create (T022,
 * T024) are added on top of this.
 */
export function ScheduleTimeline(props: ScheduleTimelineProps) {
  const abilities = props.abilities ?? READ_ONLY;
  // Research R4: below 4 px a press is a click (only a click opens a bar). The keyboard starts a
  // drag with Space and ends it with Space or Enter; a bar's own Enter handler stands down while it
  // is being dragged, so Enter keeps opening a bar that is not.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: DRAG_THRESHOLD_PX } }),
    useSensor(KeyboardSensor, {
      keyboardCodes: { start: ['Space'], cancel: ['Escape'], end: ['Space', 'Enter'] },
    }),
  );
  // The axis lock reads what the DRAGGED bar may do, so a block stays on its car and a checked-out
  // booking stays at its times, however the pointer wanders.
  const lockToAllowedAxis: Modifier = (args) => {
    const bar = (args.active?.data.current as { bar?: ScheduleBar } | undefined)?.bar;
    return axisLockModifier(bar ? allowedAxes(bar, abilities) : [])(args);
  };
  return (
    <TimelineContext
      range={props.range}
      onRangeChanged={noop}
      onResizeEnd={noop}
      usePanStrategy={noPan}
      sidebarWidth={sidebarWidthFor(window.innerWidth)}
      overlayed
      // FR-025: after a keyboard drop the confirm popover takes focus (Enter confirms). dnd-kit's
      // focus restore is turned off so it can never pull focus back to the bar afterwards; with
      // `overlayed` the bar never leaves the page, so there is nothing to restore. (The failure
      // seen at T031 was the popover focusing while still `visibility: hidden`; see
      // MoveBookingPopover.)
      accessibility={{ restoreFocus: false }}
      sensors={sensors}
      modifiers={[lockToAllowedAxis]}
    >
      <Grid {...props} />
    </TimelineContext>
  );
}

interface Placed extends ItemDefinition {
  /** A bar, or (368, Week and Month) a day cell summarising one car-day's short items. */
  bar: ScheduleBar | null;
  cell: DayCell | null;
  groupKey: string | null;
  joinedStart: boolean;
}

interface DragPreview {
  bar: ScheduleBar;
  /** 396: the dragged item's id (a copy's id when grouped by host), so only the OTHER copies are outlined. */
  activeId: string;
  proposal: DragProposal | null;
  /** 328: the clash estimate and the suggested span (the outline), for a `move`. */
  estimate: MoveEstimate | null;
  /** What the preview says, so a pointer move that changes nothing re-renders nothing. */
  key: string;
}

// 328: a `move` changes with its target car AND its shift, so both are in the key (a row change at
// the same shift must redraw the preview).
const previewKey = (bar: ScheduleBar, p: DragProposal | null) =>
  `${bar.id}|${
    p
      ? p.kind === 'car'
        ? `car:${p.targetCarId}`
        : p.kind === 'move'
          ? `move:${p.targetCarId}:${p.deltaMs}`
          : `${p.kind}:${p.deltaMs}`
      : 'none'
  }`;

function Grid({
  view,
  range,
  cars,
  bars,
  allBars,
  now,
  onOpenBar,
  onOpenDay,
  abilities = READ_ONLY,
  onProposeMove,
  onProposeResize,
  onProposeCreate,
  occupancy,
  corner,
  density = 'comfortable',
  grouping,
}: ScheduleTimelineProps) {
  const { setTimelineRef, style, pixelsToValue, sidebarWidth } = useTimelineContext();
  // 396: by the item's own id (a copy's, when grouped by host), so the card anchors to the copy in hand.
  const rectOf = (itemId: string) => {
    const el = document.querySelector<HTMLElement>(`[data-testid="schedule-bar-${itemId}"]`);
    return el?.getBoundingClientRect() ?? new DOMRect();
  };
  const [preview, setPreview] = useState<DragPreview | null>(null);

  const proposalOf = (event: DragMoveEvent | DragEndEvent): DragProposal | null => {
    const bar = (event.active.data.current as { bar?: ScheduleBar } | undefined)?.bar;
    if (!bar) return null;
    const axes = allowedAxes(bar, abilities);
    return resolveDrop({
      bar,
      view,
      abilities,
      deltaMs: pixelsToValue(event.delta.x),
      axis: lockedAxis(event.delta.x, event.delta.y, axes),
      // 396: a copy's row id carries its group; the target is the car.
      overCarId: event.over ? carIdOfRow(String(event.over.id)) : null,
      range,
      // 328 (US3): the edge clamp uses the whole rental's span, not the dragged leg's.
      span: movingSpan(bar, allBars ?? bars),
    });
  };

  useTimelineMonitor({
    onDragMove: (event) => {
      const bar = (event.active.data.current as { bar?: ScheduleBar } | undefined)?.bar;
      if (!bar) return;
      // SC-007: `onDragMove` fires on every pointer move, and a state change re-renders the grid.
      // The preview only changes once per step, so an unchanged proposal keeps the same state and
      // renders nothing (with 288 bars in Month, every move had cost 50–400 ms; measured at T031).
      const proposal = proposalOf(event as DragMoveEvent);
      // 328 (FR-006): the estimate from the loaded, unfiltered bars; it changes only with the proposal.
      const estimate = moveEstimate({ proposal, bars: allBars ?? bars, view, range, now });
      const key = `${previewKey(bar, proposal)}|${estimate?.clash ? `x:${estimate.suggestion?.start ?? '-'}` : 'ok'}`;
      const activeId = String(event.active.id);
      setPreview((prev) =>
        prev?.key === key && prev.activeId === activeId
          ? prev
          : { bar, activeId, proposal, estimate, key },
      );
    },
    onDragEnd: (event) => {
      setPreview(null);
      const proposal = proposalOf(event as DragEndEvent);
      if (!proposal || !onProposeMove) return;
      const r = event.active.rect.current.translated ?? event.active.rect.current.initial;
      onProposeMove(proposal, r ? new DOMRect(r.left, r.top, r.width, r.height) : new DOMRect());
    },
    onDragCancel: () => setPreview(null),
    onResizeEnd: (event) => {
      const bar = (event.active.data.current as { bar?: ScheduleBar } | undefined)?.bar;
      if (!bar || !onProposeResize) return;
      const proposal = resolveResize({
        bar,
        view,
        abilities,
        direction: event.direction,
        deltaMs: pixelsToValue(event.delta.x),
        range,
      });
      if (proposal) onProposeResize(proposal, rectOf(String(event.active.id)));
    },
  });

  // 316 (US4): drawing a new block. Only a caller who may edit blocks can draw, and only on the
  // empty part of a row (a press on a bar is that bar's drag).
  const drawOn = (carId: string) =>
    abilities.canEditBlocks && onProposeCreate
      ? (fromMs: number, toMs: number, anchorRect: DOMRect) => {
          const proposal = resolveCreate({
            carId,
            view,
            range,
            canEditBlocks: abilities.canEditBlocks,
            fromMs,
            toMs,
          });
          if (proposal) onProposeCreate(proposal, anchorRect);
        }
      : undefined;

  const carName = (carId: string) => {
    const car = cars.find((c) => c.id === carId);
    return car ? `${car.label} · ${car.plate_number}` : '';
  };

  const subrowsByCar = useMemo(() => {
    const carIds = new Set(cars.map((c) => c.id));
    const visible = bars.filter((b) => {
      const start = Date.parse(b.start_at);
      const end = Date.parse(b.end_at);
      return carIds.has(b.car_id) && end > range.start && start < range.end;
    });
    // A 304 extension leg belongs to its rental: both carry the rental's id as their group, and a
    // leg that starts where a bar of the same rental on the same car ends is drawn joined to it.
    const groupOf = (b: ScheduleBar) =>
      b.kind === 'booking' || b.kind === 'extension' ? (b.root_booking_id ?? b.booking_id) : null;
    // 368 (#1236, FR-001): in Week and Month, items within one Malaysia day collapse into one cell per car-day (a
    // whole-day item, so the row's stacking keeps it off a long bar on the same day); the Day view keeps every bar.
    const split = splitDayCells(visible, view);
    const placed: Placed[] = split.bars.map((b) => {
      const groupKey = groupOf(b);
      const start = Date.parse(b.start_at);
      const joinedStart =
        groupKey !== null &&
        visible.some(
          (o) =>
            o.id !== b.id &&
            o.car_id === b.car_id &&
            groupOf(o) === groupKey &&
            Date.parse(o.end_at) === start,
        );
      return {
        id: b.id,
        rowId: b.car_id,
        span: { start, end: Date.parse(b.end_at) },
        disabled: allowedAxes(b, abilities).length === 0,
        bar: b,
        cell: null,
        groupKey,
        joinedStart,
      };
    });
    for (const cell of split.cells) {
      placed.push({
        id: cell.id,
        rowId: cell.carId,
        span: { start: cell.start, end: cell.end },
        disabled: true,
        bar: null,
        cell,
        groupKey: null,
        joinedStart: false,
      });
    }
    return groupItemsToSubrows(placed, range);
  }, [bars, cars, range, abilities, view]);

  // 386 (#1278, FR-012): each car's gap after its held rentals, from the bars it already has.
  const gapsByCar = useMemo(() => {
    const out: Record<string, ReturnType<typeof gapShades>> = {};
    for (const car of cars) {
      out[car.id] = gapShades(
        bars.filter((b) => b.car_id === car.id),
        car.buffer_minutes ?? 0,
      );
    }
    return out;
  }, [bars, cars]);

  // 396: grouped by host, `copyKey` is the host group this copy is drawn in (absent otherwise: nothing changes).
  const renderCar = (car: ScheduleCar, copyKey?: string) => {
    const carLabel = `${car.label} · ${car.plate_number}`;
    const subrows = subrowsByCar[car.id] ?? [];
    const alsoWith = copyKey
      ? (car.hosts ?? []).filter((h) => h.id !== copyKey).map((h) => h.name)
      : undefined;
    // The booking being dragged in another copy of this car is outlined here.
    const twinOf = (barId: string) =>
      preview !== null && preview.bar.id === barId && preview.activeId !== copyId(barId, copyKey);
    return (
      <CarRow
        key={copyId(car.id, copyKey)}
        car={car}
        copyKey={copyKey}
        alsoWith={alsoWith}
        droppable={abilities.canMoveBooking}
        occupancy={occupancy?.cars[car.id]}
        density={density}
        range={range}
        gaps={gapsByCar[car.id]}
        suggestOutline={
          preview?.estimate?.suggestion &&
          preview.proposal?.kind === 'move' &&
          preview.proposal.targetCarId === car.id
            ? preview.estimate.suggestion
            : undefined
        }
      >
        {subrows.length === 0 ? (
          <Subrow onDraw={drawOn(car.id)} density={density}>
            {null}
          </Subrow>
        ) : (
          subrows.map((items, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: a subrow is identified by its position
            <Subrow key={i} onDraw={drawOn(car.id)} density={density}>
              {items.map((item) =>
                item.cell ? (
                  <DayCellItem
                    key={item.id}
                    cell={item.cell}
                    carLabel={carLabel}
                    view={view}
                    onOpenBar={onOpenBar}
                    onOpenDay={onOpenDay}
                  />
                ) : item.bar ? (
                  <BarItem
                    key={item.id}
                    bar={item.bar}
                    carLabel={carLabel}
                    span={item.span}
                    joinedStart={item.joinedStart}
                    groupKey={item.groupKey}
                    disabled={item.disabled ?? true}
                    resizable={canResize(item.bar, abilities, view)}
                    onOpenBar={onOpenBar}
                    copyKey={copyKey}
                    twin={twinOf(item.bar.id)}
                  />
                ) : null,
              )}
            </Subrow>
          ))
        )}
      </CarRow>
    );
  };

  return (
    <div
      ref={setTimelineRef}
      // dnd-timeline's root is `overflow: hidden`, which would make IT the scroll container for the
      // sticky car column and so pin nothing. Overflow is clipped per lane instead (CarRow).
      style={{
        ...style,
        overflow: 'visible',
        minWidth: minTimelineWidth(view, range, sidebarWidth),
      }}
      data-testid="schedule-timeline"
      data-view={view}
      data-density={density}
      // FR-002: a drag never selects page text.
      className="flex-col select-none rounded-lg border border-hairline bg-canvas"
    >
      <DayShading view={view} now={now} />
      <TimeAxis view={view} now={now} corner={corner} onOpenDay={onOpenDay} />
      {occupancy && <OccupancyRow columns={occupancy.columns} view={view} />}
      {grouping
        ? grouping.groups.map((group) => (
            <Fragment key={group.key}>
              <GroupHeader
                mode={grouping.mode}
                group={group}
                collapsed={grouping.collapsed.has(group.key)}
                onToggle={grouping.onToggle}
                density={density}
              >
                {/* 319 (US3, FR-007a): the group's own free cars, by 317's rule and cells. */}
                <FreeCells
                  columns={group.columns}
                  view={view}
                  testIdPrefix={`schedule-group-free-${group.key}-`}
                  namePrefix={group.name}
                />
              </GroupHeader>
              {/* FR-006: a collapsed group hides its rows only — its heading and cells remain. */}
              {!grouping.collapsed.has(group.key) &&
                group.cars.map((car) =>
                  renderCar(car, grouping.mode === 'host' ? group.key : undefined),
                )}
            </Fragment>
          ))
        : cars.map((car) => renderCar(car))}
      <NowMarker now={now} />
      {/* FR-001: a copy follows the pointer and says where the booking would go. 328 (#736)
          supersedes 316 FR-005: it turns red when the loaded schedule shows the target taken — an
          estimate; the platform decides on confirm. */}
      <DragOverlay dropAnimation={null}>
        {preview ? (
          <DragPreviewLabel
            clash={Boolean(preview.estimate?.clash)}
            text={
              preview.proposal?.kind === 'car'
                ? `To ${carName(preview.proposal.targetCarId)}`
                : preview.proposal
                  ? `${
                      // 328: a diagonal move names the car it would go to, as well as the times.
                      preview.proposal.kind === 'move' &&
                      preview.proposal.targetCarId !== preview.bar.car_id
                        ? `To ${carName(preview.proposal.targetCarId)} · `
                        : ''
                    }${mytPeriod(
                      Date.parse(preview.bar.start_at) + preview.proposal.deltaMs,
                      Date.parse(preview.bar.end_at) + preview.proposal.deltaMs,
                    )}`
                  : mytPeriod(Date.parse(preview.bar.start_at), Date.parse(preview.bar.end_at))
            }
          />
        ) : null}
      </DragOverlay>
    </div>
  );
}

/**
 * 328 (#736, FR-006): the copy that follows the pointer. When the loaded schedule shows the target taken
 * it turns red and says so — an estimate; the server decides on confirm. It never says "free".
 */
export function DragPreviewLabel({ text, clash }: { text: string; clash: boolean }) {
  // The copy is a dashed box the size of the booking (DragOverlay's wrapper takes the dragged bar's
  // size), and its words float just above it. Found at Gate A: words as wide as their text covered the
  // suggestion outline beside the copy. `w-max`: the words never wrap into a column on a short bar.
  return (
    <div
      data-testid="schedule-drag-preview"
      data-clash={clash ? 'true' : undefined}
      className={[
        'pointer-events-none relative h-full w-full rounded border-2 border-dashed',
        // the design system's danger tokens, which carry their own dark-mode values
        clash
          ? 'border-semantic-danger-text bg-semantic-danger-bg/70'
          : 'border-brand-blue bg-brand-blue-soft/60',
      ].join(' ')}
    >
      <span
        data-testid="schedule-drag-preview-label"
        className={[
          'absolute bottom-full left-0 mb-1 w-max whitespace-nowrap rounded px-2 py-0.5 text-xs shadow-lg',
          clash
            ? 'bg-semantic-danger-bg text-semantic-danger-text ring-1 ring-semantic-danger-text'
            : 'bg-canvas text-ink ring-1 ring-brand-blue',
        ].join(' ')}
      >
        {clash ? `Clashes · ${text}` : text}
      </span>
    </div>
  );
}
